import axios from 'axios'
import { apiCache } from '../utils/apiCache'
import warehouseGraphQL from './warehouseGraphQL'

/**
 * DEPRECATION NOTICE (January 2026):
 * This service is being deprecated in favor of GraphQL endpoints via warehouseGraphQL.ts
 * 
 * The Odoo direct API connection is no longer used due to credential/access changes.
 * Methods in this file are being updated to either:
 * 1. Use GraphQL endpoints as the data source (recommended)
 * 2. Return cached data or graceful fallbacks
 * 3. Throw clear deprecation errors for write operations
 * 
 * For new development, use warehouseGraphQL.ts instead.
 */

export interface OdooProduct {
  id: number
  name: string
  list_price: number
  qty_available: number
  write_date: string
  display_name: string
  categ_id: [number, string]
  default_code?: string
  barcode?: string
  cost?: number
  cost_price?: number      // Cost price field
  cardnado_t1_price?: number // Cardnado T1 pricing tier
  poke_unlimited_pricelist?: number // Poke Unlimited Pricelist from Odoo
  ebay_avg_price?: number  // Legacy eBay average price field
  product_avg_price?: number  // New: Overall product average from all sellers
  quality_seller_avg_price?: number  // New: Average from quality sellers only
  image_1920?: string      // Add base64 image field
  listing_name?: string    // Search phrase from poke_unlimited_listings
}

export interface OdooProductWithEbay extends OdooProduct {
  ebay_avg_price: number
  ebay_price_updated?: string
  cleaned_name?: string // Name without (S- prefix for eBay search
}

export interface OdooInventoryData {
  products: OdooProduct[]
  lastUpdated: string
  totalCount: number
}

// @ts-ignore - Vite environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://poketrade.redexct.xyz'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,  // Increased to 60 seconds for remote users
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for auth
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Odoo API Error:', error)
    return Promise.reject(error)
  }
)

class OdooApiService {
  /**
   * @deprecated Odoo connection is no longer used. Returns false always.
   * Use warehouseGraphQL.getSummary() to test GraphQL connectivity instead.
   */
  async testConnection(): Promise<boolean> {
    console.warn('⚠️ DEPRECATED: odooApiService.testConnection() - Odoo API is no longer used')
    // Always return false since Odoo credentials are deprecated
    return false
  }

  /**
   * @deprecated Use warehouseGraphQL.getProducts() instead
   * This method now uses GraphQL as the data source instead of the Odoo API
   */
  async getInventoryData(): Promise<OdooInventoryData> {
    const cacheKey = 'inventory_data'
    
    // Try to get from cache first
    const cachedData = apiCache.get<OdooInventoryData>(cacheKey)
    if (cachedData) {
      console.log('📦 Cache hit for inventory data')
      return cachedData
    }

    try {
      console.log('🔄 Using GraphQL for inventory data instead of deprecated Odoo API')
      
      // Use GraphQL endpoint to get products
      const response = await warehouseGraphQL.getProducts(500, 0)
      
      // Transform GraphQL response to match expected OdooInventoryData format
      const inventoryData: OdooInventoryData = {
        products: response.edges.map(p => ({
          id: p.productId,
          name: p.name,
          list_price: p.listPrice ? parseFloat(p.listPrice) : 0,
          qty_available: p.qtyAvailable,
          write_date: p.lastUpdated || new Date().toISOString(),
          display_name: p.name,
          categ_id: [0, p.category || 'Uncategorized'] as [number, string],
          default_code: p.sku || undefined,
          barcode: p.barcode || undefined,
          cost: p.costPrice ? parseFloat(p.costPrice) : undefined,
          cost_price: p.costPrice ? parseFloat(p.costPrice) : undefined,
          ebay_avg_price: p.ebayAvgPrice ? parseFloat(p.ebayAvgPrice) : undefined,
          product_avg_price: p.marketPrice ? parseFloat(p.marketPrice) : undefined,
          image_1920: p.imageUrl || undefined
        })),
        lastUpdated: new Date().toISOString(),
        totalCount: response.totalCount
      }
      
      // Cache for 3 minutes
      apiCache.set(cacheKey, inventoryData, 3 * 60 * 1000)
      return inventoryData
    } catch (error) {
      console.error('Failed to fetch inventory data from GraphQL:', error)
      throw error
    }
  }

  /**
   * @deprecated Use warehouseGraphQL.getProducts() instead
   * This method now uses GraphQL as the data source instead of the Odoo API
   */
  async getInventoryWithEbayPrices(useCache: boolean = true): Promise<any> {
    const cacheKey = 'inventory_ebay_prices'
    
    // Try to get from cache first (only if useCache is true)
    if (useCache) {
      const cachedData = apiCache.get<any>(cacheKey)
      if (cachedData) {
        console.log('📦 Cache hit for inventory with eBay prices')
        return cachedData
      }
    }

    try {
      console.log(`🔄 Using GraphQL for inventory instead of deprecated Odoo API (useCache=${useCache})`)
      
      // Use GraphQL endpoint to get products with all pricing data
      const response = await warehouseGraphQL.getProducts(500, 0)
      
      // Transform GraphQL response to match expected Odoo format
      const result = {
        products: response.edges.map(p => ({
          id: p.productId,
          name: p.name,
          list_price: p.listPrice ? parseFloat(p.listPrice) : 0,
          qty_available: p.qtyAvailable,
          write_date: p.lastUpdated || new Date().toISOString(),
          display_name: p.name,
          categ_id: [0, p.category || 'Uncategorized'],
          default_code: p.sku,
          barcode: p.barcode,
          cost: p.costPrice ? parseFloat(p.costPrice) : undefined,
          cost_price: p.costPrice ? parseFloat(p.costPrice) : undefined,
          ebay_avg_price: p.ebayAvgPrice ? parseFloat(p.ebayAvgPrice) : 0,
          product_avg_price: p.marketPrice ? parseFloat(p.marketPrice) : 0,
          cardnado_t1_price: p.cardnadoT1Price ? parseFloat(p.cardnadoT1Price) : 0,
          poke_unlimited_pricelist: p.pokeUnlimitedPricelist ? parseFloat(p.pokeUnlimitedPricelist) : 0,
          inventory_value: p.inventoryValue ? parseFloat(p.inventoryValue) : 0,
          image_1920: p.imageUrl,
          // Sales data from GraphQL
          sold_7d: p.sold7d,
          sold_30d: p.sold30d,
          sold_90d: p.sold90d,
          velocity_daily: p.velocityDaily,
          momentum: p.momentum,
          stock_status: p.stockStatus,
          days_of_stock: p.daysOfStock
        })),
        total_count: response.totalCount,
        last_updated: new Date().toISOString(),
        source: 'graphql' // Indicate this comes from GraphQL, not Odoo
      }
      
      // Only cache if useCache is true
      if (useCache) {
        apiCache.set(cacheKey, result, 5 * 60 * 1000)
      }
      return result
    } catch (error) {
      console.error('Failed to fetch inventory with eBay prices from GraphQL:', error)
      throw error
    }
  }

  /**
   * @deprecated Use warehouseGraphQL.getProducts() with a search filter instead
   * This method now uses GraphQL as the data source instead of the Odoo API
   */
  async getProduct(productId: number, forceRefresh: boolean = false): Promise<OdooProduct | null> {
    const cacheKey = `product_${productId}`
    
    // Try to get from cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedProduct = apiCache.get<OdooProduct>(cacheKey)
      if (cachedProduct) {
        console.log(`📦 Cache hit for product ${productId}`)
        return cachedProduct
      }
    }

    try {
      console.log(`🔄 Using GraphQL for product ${productId} instead of deprecated Odoo API ${forceRefresh ? '(FORCE REFRESH)' : ''}`)
      
      // Fetch products from GraphQL and find the one with matching ID
      // First try with a reasonable limit
      const response = await warehouseGraphQL.getProducts(500, 0)
      const graphqlProduct = response.edges.find(p => p.productId === productId)
      
      if (graphqlProduct) {
        // Transform GraphQL response to match OdooProduct format
        const product: OdooProduct = {
          id: graphqlProduct.productId,
          name: graphqlProduct.name,
          list_price: graphqlProduct.listPrice ? parseFloat(graphqlProduct.listPrice) : 0,
          qty_available: graphqlProduct.qtyAvailable,
          write_date: graphqlProduct.lastUpdated || new Date().toISOString(),
          display_name: graphqlProduct.name,
          categ_id: [0, graphqlProduct.category || 'Uncategorized'],
          default_code: graphqlProduct.sku || undefined,
          barcode: graphqlProduct.barcode || undefined,
          cost: graphqlProduct.costPrice ? parseFloat(graphqlProduct.costPrice) : undefined,
          cost_price: graphqlProduct.costPrice ? parseFloat(graphqlProduct.costPrice) : undefined,
          ebay_avg_price: graphqlProduct.ebayAvgPrice ? parseFloat(graphqlProduct.ebayAvgPrice) : undefined,
          product_avg_price: graphqlProduct.marketPrice ? parseFloat(graphqlProduct.marketPrice) : undefined,
          cardnado_t1_price: graphqlProduct.cardnadoT1Price ? parseFloat(graphqlProduct.cardnadoT1Price) : undefined,
          poke_unlimited_pricelist: graphqlProduct.pokeUnlimitedPricelist ? parseFloat(graphqlProduct.pokeUnlimitedPricelist) : undefined,
          image_1920: graphqlProduct.imageUrl || undefined
        }
        
        // Cache in frontend for 10 minutes (unless force refresh - then cache for 1 minute)
        apiCache.set(cacheKey, product, forceRefresh ? 60 * 1000 : 10 * 60 * 1000)
        return product
      }
      
      console.log(`Product ${productId} not found in GraphQL data`)
      return null
    } catch (error: any) {
      console.error('Failed to fetch product from GraphQL:', error)
      return null
    }
  }

  /**
   * @deprecated Use warehouseGraphQL.getProducts() with a search filter instead
   * This method now uses GraphQL as the data source instead of the Odoo API
   */
  async searchProducts(searchTerm: string): Promise<OdooProduct[]> {
    try {
      console.log(`🔄 Using GraphQL for product search instead of deprecated Odoo API: "${searchTerm}"`)
      
      // Use GraphQL with search filter
      const response = await warehouseGraphQL.getProducts(100, 0, { search: searchTerm })
      
      // Transform to OdooProduct format
      return response.edges.map(p => ({
        id: p.productId,
        name: p.name,
        list_price: p.listPrice ? parseFloat(p.listPrice) : 0,
        qty_available: p.qtyAvailable,
        write_date: p.lastUpdated || new Date().toISOString(),
        display_name: p.name,
        categ_id: [0, p.category || 'Uncategorized'] as [number, string],
        default_code: p.sku || undefined,
        barcode: p.barcode || undefined,
        cost: p.costPrice ? parseFloat(p.costPrice) : undefined,
        cost_price: p.costPrice ? parseFloat(p.costPrice) : undefined,
        ebay_avg_price: p.ebayAvgPrice ? parseFloat(p.ebayAvgPrice) : undefined,
        product_avg_price: p.marketPrice ? parseFloat(p.marketPrice) : undefined,
        cardnado_t1_price: p.cardnadoT1Price ? parseFloat(p.cardnadoT1Price) : undefined,
        poke_unlimited_pricelist: p.pokeUnlimitedPricelist ? parseFloat(p.pokeUnlimitedPricelist) : undefined,
        image_1920: p.imageUrl || undefined
      }))
    } catch (error) {
      console.error('Failed to search products via GraphQL:', error)
      throw error
    }
  }

  /**
   * @deprecated Odoo write operations are no longer supported
   * Product quantity updates must be done directly in Odoo or through an alternative system
   */
  async updateProductQuantity(productId: number, newQuantity: number): Promise<boolean> {
    console.error('⚠️ DEPRECATED: updateProductQuantity() - Odoo write operations are no longer supported')
    console.error(`Attempted to update product ${productId} quantity to ${newQuantity}`)
    throw new Error('Odoo write operations are no longer supported. Please update quantities directly in Odoo.')
  }

  /**
   * @deprecated Odoo write operations are no longer supported
   * Product price updates must be done directly in Odoo or through an alternative system
   */
  async updateProductPrice(productId: number, newPrice: number): Promise<boolean> {
    console.error('⚠️ DEPRECATED: updateProductPrice() - Odoo write operations are no longer supported')
    console.error(`Attempted to update product ${productId} price to ${newPrice}`)
    throw new Error('Odoo write operations are no longer supported. Please update prices directly in Odoo.')
  }

  /**
   * @deprecated Odoo connection is no longer used
   */
  async getHealthStatus(): Promise<{ status: string; odoo_connected: boolean }> {
    console.warn('⚠️ DEPRECATED: getHealthStatus() - Odoo API is no longer used')
    return { status: 'deprecated', odoo_connected: false }
  }

  // Clean product name for eBay search by removing language indicators and other noise
  cleanProductNameForEbay(productName: string): string {
    // Remove language indicators with comprehensive patterns
    const patternsToRemove = [
      /\(S-[^)]*\)/gi,          // (S-Chinese), (S-Japanese), etc.
      /\([^)]*Chinese[^)]*\)/gi, // (Chinese), (Traditional Chinese), etc.
      /\([^)]*Japanese[^)]*\)/gi, // (Japanese), (JP), etc.
      /\([^)]*Korean[^)]*\)/gi,  // (Korean), (KR), etc.
      /\([^)]*Thai[^)]*\)/gi,    // (Thai), (TH), etc.
      /\(JP\)/gi,               // (JP)
      /\(KR\)/gi,               // (KR)
      /\(CN\)/gi,               // (CN)
      /\(TH\)/gi,               // (TH)
      /\(EN\)/gi,               // (EN)
    ]
    
    let cleanedName = productName
    patternsToRemove.forEach(pattern => {
      cleanedName = cleanedName.replace(pattern, '')
    })
    
    // Clean up extra whitespace and trailing punctuation
    cleanedName = cleanedName.replace(/\s+/g, ' ').trim()
    cleanedName = cleanedName.replace(/[\s\-,:;]+$/, '')
    
    return cleanedName
  }

  // Get eBay average price for a single product using enhanced competitive analysis
  async getEbayPriceForProduct(product: OdooProduct): Promise<number> {
    try {
      const cleanedName = this.cleanProductNameForEbay(product.name)
      console.log(`🔍 Fetching enhanced eBay price for: "${cleanedName}" (original: "${product.name}")`)
      
      // Use the new simplified market service with cost-based validation
      const refreshResponse = await api.post('/api/v1/simplified-market/refresh-product-pricing', {
        odoo_product_id: product.id
      }, {
        timeout: 25000 // 25 second timeout for enhanced search
      })

      if (refreshResponse.data.success && refreshResponse.data.data) {
        const pricingData = refreshResponse.data.data
        const avgPrice = pricingData.ebay_avg_price
        const confidence = pricingData.market_confidence_score || 0
        const competitiveSellers = pricingData.competitive_sellers_count || 0
        const filteredOutliers = pricingData.filtered_outliers || 0
        
        console.log(`✅ Enhanced pricing for "${cleanedName}": $${avgPrice.toFixed(2)}`)
        console.log(`📊 Competitive sellers: ${competitiveSellers}, Confidence: ${(confidence * 100).toFixed(1)}%`)
        console.log(`� Total analyzed: ${pricingData.total_listings_analyzed}, Filtered outliers: ${filteredOutliers}`)
        
        if (product.cost && product.cost > 0) {
          const markup = avgPrice / product.cost
          console.log(`💰 Markup over cost: ${markup.toFixed(1)}x (cost: $${product.cost.toFixed(2)})`)
        }
        
        return avgPrice
        
      } else {
        console.log(`❌ No enhanced pricing data available for "${cleanedName}"`)
        return 0
      }
      
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        console.error(`⏱️ Timeout getting enhanced eBay price for "${product.name}"`)
      } else {
        console.error(`❌ Error getting enhanced eBay price for "${product.name}":`, error.response?.data || error.message)
      }
      return 0
    }
  }

  // Get eBay prices for multiple products (with improved rate limiting and error handling)
  async getEbayPricesForProducts(products: OdooProduct[]): Promise<OdooProductWithEbay[]> {
    const productsWithEbay: OdooProductWithEbay[] = []
    
    console.log(`🚀 Starting eBay price lookup for ${products.length} products...`)
    
    for (let i = 0; i < products.length; i++) {
      const product = products[i]
      try {
        console.log(`\n📦 Processing ${i + 1}/${products.length}: ${product.name}`)
        const ebayPrice = await this.getEbayPriceForProduct(product)
        
        productsWithEbay.push({
          ...product,
          ebay_avg_price: ebayPrice,
          ebay_price_updated: new Date().toISOString(),
          cleaned_name: this.cleanProductNameForEbay(product.name)
        })
        
        // Progressive rate limiting: slower for more API calls
        const delayMs = Math.min(500 + (i * 10), 2000) // 500ms to 2s delay
        if (i < products.length - 1) {
          console.log(`⏳ Waiting ${delayMs}ms before next request...`)
          await new Promise(resolve => setTimeout(resolve, delayMs))
        }
        
        // Log progress every 5 items
        if ((i + 1) % 5 === 0) {
          const successCount = productsWithEbay.filter(p => p.ebay_avg_price > 0).length
          const successRate = (successCount / (i + 1)) * 100
          console.log(`📊 Progress: ${i + 1}/${products.length} processed, ${successCount} prices found (${successRate.toFixed(1)}% success rate)`)
        }
        
      } catch (error) {
        console.error(`💥 Error processing product ${product.name}:`, error)
        // Add product without eBay price
        productsWithEbay.push({
          ...product,
          ebay_avg_price: 0,
          cleaned_name: this.cleanProductNameForEbay(product.name)
        })
      }
    }
    
    const successCount = productsWithEbay.filter(p => p.ebay_avg_price > 0).length
    const successRate = (successCount / productsWithEbay.length) * 100
    console.log(`✅ Completed eBay price lookup: ${successCount}/${productsWithEbay.length} products found prices (${successRate.toFixed(1)}% success rate)`)
    
    return productsWithEbay
  }

  async getProductChartData(productId: number): Promise<any> {
    try {
      const response = await api.get(`/api/odoo/products/${productId}/chart-data`)
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Chart data not available for this product')
      }
      console.error('Failed to fetch product chart data:', error)
      throw error
    }
  }

  async getProductsWithEbayData(): Promise<number[]> {
    try {
      const response = await api.get('/api/odoo/products-with-ebay-data')
      return response.data.product_ids || []
    } catch (error) {
      console.error('Failed to fetch products with eBay data:', error)
      return []
    }
  }

  async fetchAllEbayPricing(): Promise<any> {
    try {
      const response = await api.post('/api/odoo/inventory/fetch-all-pricing');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch all eBay prices:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use warehouseGraphQL.getStockLevels() instead
   * This method now uses GraphQL as the data source instead of the Odoo API
   */
  async getStockLevels(sortBy: string = 'quantity', viewMode: string = 'top', limit: number = 10): Promise<any> {
    const cacheKey = `stock_levels_${sortBy}_${viewMode}_${limit}`
    
    // Try to get from cache first (cache for 2 minutes since it's chart data)
    const cachedData = apiCache.get<any>(cacheKey)
    if (cachedData) {
      console.log(`📦 Cache hit for stock levels (${sortBy}, ${viewMode}, ${limit})`)
      return cachedData
    }

    try {
      console.log(`🔄 Using GraphQL for stock levels instead of deprecated Odoo API (${sortBy}, ${viewMode}, ${limit})`)
      
      // Use GraphQL endpoint instead of Odoo API
      const graphqlData = await warehouseGraphQL.getStockLevels(
        sortBy as 'quantity' | 'value',
        viewMode as 'top' | 'bottom',
        limit
      )
      
      // Transform to expected format
      const result = {
        products: graphqlData.products,
        total_count: graphqlData.total_count,
        generated_at: graphqlData.generated_at
      }
      
      // Cache for 2 minutes - shorter cache for dynamic chart data
      apiCache.set(cacheKey, result, 2 * 60 * 1000)
      return result
    } catch (error) {
      console.error('Failed to fetch stock levels from GraphQL:', error)
      throw error
    }
  }
}

export const odooApiService = new OdooApiService()
